import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchStoreCategoryConfig } from '../../ducks/user.duck';
import { ensureCurrentUser } from '../../util/data';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import { getStoreCategorySyncStatus, getStoreCategoryCounts, saveStoreCategoriesDelta, saveStoreCategory, assignListings } from '../../util/api';
import { SAVE_STORE_CATEGORIES_TIMEOUT_MS, STORE_CATEGORY_SYNC_DELAY_MS, ASSIGN_LISTINGS_MAX_BATCH_SIZE } from './constants';
import { getCategoryDepth, getAllDescendants } from './StoreCategories/helpers';

const stripCategoryFieldsFromUserPublicData = user => {
    if (!user?.attributes?.profile) return user;
    const prevPd = user.attributes.profile.publicData || {};
    const {
        storeCategories: _sc,
        storeCategoryGroups: _sg,
        showAllCategories: _s1,
        showAllCustomCategories: _s2,
        showAllInCategoryFilter: _s3,
        ...restPd
    } = prevPd;
    return {
        ...user,
        attributes: {
            ...user.attributes,
            profile: {
                ...user.attributes.profile,
                publicData: restPd,
            },
        },
    };
};

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/StoreCategoriesPage/SET_INITIAL_STATE';

export const LOAD_CATEGORIES = 'app/StoreCategoriesPage/LOAD_CATEGORIES';
export const LOAD_GROUPS = 'app/StoreCategoriesPage/LOAD_GROUPS';
export const CREATE_CATEGORY = 'app/StoreCategoriesPage/CREATE_CATEGORY';
export const UPDATE_CATEGORY = 'app/StoreCategoriesPage/UPDATE_CATEGORY';
export const DELETE_CATEGORY = 'app/StoreCategoriesPage/DELETE_CATEGORY';
export const TOGGLE_CATEGORY_VISIBILITY = 'app/StoreCategoriesPage/TOGGLE_CATEGORY_VISIBILITY';
export const REORDER_CATEGORIES = 'app/StoreCategoriesPage/REORDER_CATEGORIES';
export const SET_GROUP_VISIBLE = 'app/StoreCategoriesPage/SET_GROUP_VISIBLE';
export const REORDER_GROUPS = 'app/StoreCategoriesPage/REORDER_GROUPS';
export const BULK_SET_VISIBILITY = 'app/StoreCategoriesPage/BULK_SET_VISIBILITY';

export const SAVE_REQUEST = 'app/StoreCategoriesPage/SAVE_REQUEST';
export const SAVE_SUCCESS = 'app/StoreCategoriesPage/SAVE_SUCCESS';
export const SAVE_ERROR = 'app/StoreCategoriesPage/SAVE_ERROR';
export const LOAD_REQUEST = 'app/StoreCategoriesPage/LOAD_REQUEST';
export const LOAD_SUCCESS = 'app/StoreCategoriesPage/LOAD_SUCCESS';
export const LOAD_ERROR = 'app/StoreCategoriesPage/LOAD_ERROR';
export const LOAD_COUNTS_REQUEST = 'app/StoreCategoriesPage/LOAD_COUNTS_REQUEST';
export const LOAD_COUNTS_SUCCESS = 'app/StoreCategoriesPage/LOAD_COUNTS_SUCCESS';
export const LOAD_COUNTS_ERROR = 'app/StoreCategoriesPage/LOAD_COUNTS_ERROR';
export const LOAD_SYNC_STATUS_REQUEST = 'app/StoreCategoriesPage/LOAD_SYNC_STATUS_REQUEST';
export const LOAD_SYNC_STATUS_SUCCESS = 'app/StoreCategoriesPage/LOAD_SYNC_STATUS_SUCCESS';
export const LOAD_SYNC_STATUS_ERROR = 'app/StoreCategoriesPage/LOAD_SYNC_STATUS_ERROR';

export const SET_PENDING_LISTING_ASSIGNMENTS = 'app/StoreCategoriesPage/SET_PENDING_LISTING_ASSIGNMENTS';
export const CLEAR_PENDING_LISTING_ASSIGNMENTS = 'app/StoreCategoriesPage/CLEAR_PENDING_LISTING_ASSIGNMENTS';
export const CLEAR_PENDING_FOR_CATEGORY = 'app/StoreCategoriesPage/CLEAR_PENDING_FOR_CATEGORY';
export const SET_LISTING_COUNT_OVERRIDES = 'app/StoreCategoriesPage/SET_LISTING_COUNT_OVERRIDES';
export const SET_COUNTS_FROM_SAVE = 'app/StoreCategoriesPage/SET_COUNTS_FROM_SAVE';
export const SET_LAST_SAVED = 'app/StoreCategoriesPage/SET_LAST_SAVED';
export const SET_SHOW_ALL_CATEGORIES = 'app/StoreCategoriesPage/SET_SHOW_ALL_CATEGORIES';
export const SET_SHOW_ALL_CUSTOM_CATEGORIES = 'app/StoreCategoriesPage/SET_SHOW_ALL_CUSTOM_CATEGORIES';
export const SET_SHOW_ALL_IN_CATEGORY_FILTER = 'app/StoreCategoriesPage/SET_SHOW_ALL_IN_CATEGORY_FILTER';
export const INCREMENT_LISTING_ASSIGNMENT_REFRESH = 'app/StoreCategoriesPage/INCREMENT_LISTING_ASSIGNMENT_REFRESH';
export const ADD_ASSIGNMENT_FAILURES = 'app/StoreCategoriesPage/ADD_ASSIGNMENT_FAILURES';
export const DISMISS_ASSIGNMENT_FAILURE = 'app/StoreCategoriesPage/DISMISS_ASSIGNMENT_FAILURE';
export const CLEAR_ASSIGNMENT_FAILURES = 'app/StoreCategoriesPage/CLEAR_ASSIGNMENT_FAILURES';

// ================ Reducer ================ //

const initialState = {
    categories: [],
    groups: {},
    showAllCategories: true,
    showAllCustomCategories: true,
    showAllInCategoryFilter: true,
    lastSavedCategories: [],
    lastSavedGroups: {},
    lastSavedShowAllCategories: true,
    lastSavedShowAllCustomCategories: true,
    lastSavedShowAllInCategoryFilter: true,
    saveInProgress: false,
    saveError: null,
    loadInProgress: false,
    loadError: null,
    countsById: {},
    groupCountsByMpOption: {},
    countsInProgress: false,
    countsError: null,
    syncStatus: {
        syncing: false,
        status: 'idle',
        isStuck: false,
        suggestRetry: false,
    },
    syncStatusInProgress: false,
    syncStatusError: null,
    pendingListingAssignments: {}, // { [categoryId]: { assignListingIds: string[], unassignListingIds: string[], forceReassignListingIds?: string[] } }
    listingAssignmentRefreshKey: 1, // Incremented on save success so listing assignment UI refetches hits
    listingCountOverridesById: {}, // { [categoryId]: number } – expected count after save until loadCounts returns
    assignmentFailures: [], // [{ id, listingId, title, categoryId, type, message }]
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case SET_INITIAL_STATE:
            return { ...initialState };
        case LOAD_CATEGORIES: {
            const categories = (payload || []).map(c => {
                const names = c.names || (c.name ? { en: c.name } : {});
                const name = c.name || names.en || '';
                return { ...c, names, name };
            });
            return { ...state, categories, lastSavedCategories: categories };
        }
        case LOAD_GROUPS: {
            const categories = state.categories || [];
            const incomingGroups = payload || {};
            const normalized = normalizeGroups(incomingGroups, categories);
            return { ...state, groups: normalized, lastSavedGroups: normalized };
        }
        case CREATE_CATEGORY:
            return { ...state, categories: [...state.categories, payload] };
        case UPDATE_CATEGORY: {
            const { id, data } = payload;
            const categories = state.categories.map(c => {
                if (c.id !== id) return c;
                const next = { ...c, ...data };
                if (data && data.names) {
                    next.names = { ...(c.names || {}), ...(data.names || {}) };
                }
                // keep legacy name in sync if names.en provided
                if (data?.names?.en && !data?.name) next.name = data.names.en;
                return next;
            });
            // Note: Children automatically move with parent because they reference the category by ID
            // No need to update children's parentId - they already point to the moved category
            const nextGroups = normalizeGroups(state.groups || {}, categories);
            return { ...state, categories, groups: nextGroups };
        }
        case DELETE_CATEGORY: {
            const { id } = payload;
            const deletedCategory = state.categories.find(c => c.id === id);
            if (!deletedCategory) return state;

            // Get deleted category's parent
            const deletedParentId = deletedCategory.parentId;

            // Reparent all children to deleted category's parent and remove deleted category
            // Note: Depth validation is done in the UI before dispatching this action
            const categories = state.categories
                .map(c => {
                    if (c.id === id) {
                        // Remove deleted category
                        return null;
                    }
                    // Reparent direct children
                    if (c.parentId === `sc:${id}` || c.parentId === id) {
                        return { ...c, parentId: deletedParentId };
                    }
                    return c;
                })
                .filter(Boolean); // Remove null entries

            const nextGroups = normalizeGroups(state.groups || {}, categories);
            const nextPending = { ...(state.pendingListingAssignments || {}) };
            delete nextPending[id];
            return { ...state, categories, groups: nextGroups, pendingListingAssignments: nextPending };
        }
        case TOGGLE_CATEGORY_VISIBILITY: {
            const { id, visible } = payload;
            const categories = state.categories.map(c => (c.id === id ? { ...c, visible } : c));
            return { ...state, categories };
        }
        case BULK_SET_VISIBILITY: {
            const { ids, visible } = payload;
            const idSet = new Set(ids || []);
            const categories = state.categories.map(c => (idSet.has(c.id) ? { ...c, visible } : c));
            return { ...state, categories };
        }
        case REORDER_CATEGORIES:
            return { ...state, categories: payload };
        case REORDER_GROUPS: {
            // Always normalize to avoid stale/empty keys
            const normalized = normalizeGroups(payload || {}, state.categories || []);
            return { ...state, groups: normalized };
        }
        case SET_GROUP_VISIBLE: {
            const { mpKey, visible } = payload;
            // Do not resurrect empty groups
            const hasChildren = (state.categories || []).some(c => c.parentId === mpKey);
            if (!hasChildren) return state;
            return {
                ...state,
                groups: { ...state.groups, [mpKey]: { ...(state.groups?.[mpKey] || {}), visible } },
            };
        }
        case SAVE_REQUEST:
            return { ...state, saveInProgress: true, saveError: null };
        case SAVE_SUCCESS:
            return { ...state, saveInProgress: false };
        case SAVE_ERROR:
            return { ...state, saveInProgress: false, saveError: payload };
        case LOAD_REQUEST:
            return { ...state, loadInProgress: true, loadError: null };
        case LOAD_SUCCESS:
            return { ...state, loadInProgress: false };
        case LOAD_ERROR:
            return { ...state, loadInProgress: false, loadError: payload };
        case LOAD_COUNTS_REQUEST:
            return { ...state, countsInProgress: true, countsError: null };
        case LOAD_COUNTS_SUCCESS: {
            const { countsById = {}, groupCountsByMpOption = {} } = payload || {};
            return { ...state, countsInProgress: false, countsById, groupCountsByMpOption, listingCountOverridesById: {} };
        }
        case LOAD_COUNTS_ERROR:
            return { ...state, countsInProgress: false, countsError: payload };
        case LOAD_SYNC_STATUS_REQUEST:
            return { ...state, syncStatusInProgress: true, syncStatusError: null };
        case LOAD_SYNC_STATUS_SUCCESS:
            return { ...state, syncStatusInProgress: false, syncStatus: payload || {} };
        case LOAD_SYNC_STATUS_ERROR:
            return { ...state, syncStatusInProgress: false, syncStatusError: payload };
        case SET_PENDING_LISTING_ASSIGNMENTS: {
            const { categoryId, assignListingIds, unassignListingIds, forceReassignListingIds } = payload || {};
            if (!categoryId) return state;
            return {
                ...state,
                pendingListingAssignments: {
                    ...state.pendingListingAssignments,
                    [categoryId]: {
                        assignListingIds: assignListingIds || [],
                        unassignListingIds: unassignListingIds || [],
                        forceReassignListingIds: forceReassignListingIds || [],
                    },
                },
            };
        }
        case CLEAR_PENDING_LISTING_ASSIGNMENTS:
            return { ...state, pendingListingAssignments: {} };
        case CLEAR_PENDING_FOR_CATEGORY: {
            const { categoryId } = payload || {};
            if (!categoryId) return state;
            const next = { ...(state.pendingListingAssignments || {}) };
            delete next[categoryId];
            return { ...state, pendingListingAssignments: next };
        }
        case SET_LISTING_COUNT_OVERRIDES: {
            const overrides = (payload && payload.overrides) || {};
            return { ...state, listingCountOverridesById: overrides };
        }
        case SET_COUNTS_FROM_SAVE: {
            const { countsById: countsFromSave = {}, groupCountsByMpOption: groupCountsFromSave = {} } = payload || {};
            return {
                ...state,
                countsById: countsFromSave,
                groupCountsByMpOption: groupCountsFromSave,
                listingCountOverridesById: {},
            };
        }
        case SET_LAST_SAVED: {
            const { categories: savedCategories, groups: savedGroups, showAllCategories: savedShowAll, showAllCustomCategories: savedShowAllCustom, showAllInCategoryFilter: savedShowAllInFilter } = payload || {};
            return {
                ...state,
                lastSavedCategories: savedCategories != null ? savedCategories : state.lastSavedCategories,
                lastSavedGroups: savedGroups != null ? savedGroups : state.lastSavedGroups,
                lastSavedShowAllCategories: savedShowAll != null ? savedShowAll : state.lastSavedShowAllCategories,
                lastSavedShowAllCustomCategories: savedShowAllCustom != null ? savedShowAllCustom : state.lastSavedShowAllCustomCategories,
                lastSavedShowAllInCategoryFilter: savedShowAllInFilter != null ? savedShowAllInFilter : state.lastSavedShowAllInCategoryFilter,
            };
        }
        case SET_SHOW_ALL_IN_CATEGORY_FILTER: {
            const { showAllInCategoryFilter } = payload || {};
            return typeof showAllInCategoryFilter === 'boolean' ? { ...state, showAllInCategoryFilter } : state;
        }
        case SET_SHOW_ALL_CATEGORIES: {
            const { showAllCategories } = payload || {};
            return typeof showAllCategories === 'boolean' ? { ...state, showAllCategories } : state;
        }
        case SET_SHOW_ALL_CUSTOM_CATEGORIES: {
            const { showAllCustomCategories } = payload || {};
            return typeof showAllCustomCategories === 'boolean' ? { ...state, showAllCustomCategories } : state;
        }
        case INCREMENT_LISTING_ASSIGNMENT_REFRESH:
            return { ...state, listingAssignmentRefreshKey: (state.listingAssignmentRefreshKey || 0) + 1 };
        case ADD_ASSIGNMENT_FAILURES: {
            const incoming = Array.isArray(payload) ? payload : [];
            if (incoming.length === 0) return state;
            return { ...state, assignmentFailures: [...(state.assignmentFailures || []), ...incoming] };
        }
        case DISMISS_ASSIGNMENT_FAILURE: {
            const { id } = payload || {};
            if (!id) return state;
            return { ...state, assignmentFailures: (state.assignmentFailures || []).filter(x => x.id !== id) };
        }
        case CLEAR_ASSIGNMENT_FAILURES:
            return { ...state, assignmentFailures: [] };
        default:
            return state;
    }
}

// ================ Action creators ================ //

export const setInitialState = () => ({ type: SET_INITIAL_STATE });
export const loadCategories = categories => ({ type: LOAD_CATEGORIES, payload: categories });
export const loadGroups = groups => ({ type: LOAD_GROUPS, payload: groups });
export const createCategory = category => ({ type: CREATE_CATEGORY, payload: category });
export const updateCategory = (id, data) => ({ type: UPDATE_CATEGORY, payload: { id, data } });
export const deleteCategory = id => ({ type: DELETE_CATEGORY, payload: { id } });
export const toggleCategoryVisibility = (id, visible) => ({
    type: TOGGLE_CATEGORY_VISIBILITY,
    payload: { id, visible },
});
export const reorderCategories = categories => ({ type: REORDER_CATEGORIES, payload: categories });
export const setGroupVisible = (mpKey, visible) => ({ type: SET_GROUP_VISIBLE, payload: { mpKey, visible } });
export const reorderGroups = groups => ({ type: REORDER_GROUPS, payload: groups });
export const bulkSetVisibility = (ids, visible) => ({ type: BULK_SET_VISIBILITY, payload: { ids, visible } });

export const saveRequest = () => ({ type: SAVE_REQUEST });
export const saveSuccess = () => ({ type: SAVE_SUCCESS });
export const saveError = e => ({ type: SAVE_ERROR, payload: e, error: true });
export const loadRequest = () => ({ type: LOAD_REQUEST });
export const loadSuccess = () => ({ type: LOAD_SUCCESS });
export const loadError = e => ({ type: LOAD_ERROR, payload: e, error: true });
export const loadCountsRequest = () => ({ type: LOAD_COUNTS_REQUEST });
export const loadCountsSuccess = payload => ({ type: LOAD_COUNTS_SUCCESS, payload });
export const loadCountsError = e => ({ type: LOAD_COUNTS_ERROR, payload: e, error: true });
export const setPendingListingAssignments = (categoryId, assignListingIds, unassignListingIds, forceReassignListingIds = []) => ({
    type: SET_PENDING_LISTING_ASSIGNMENTS,
    payload: { categoryId, assignListingIds, unassignListingIds, forceReassignListingIds },
});
export const clearPendingListingAssignments = () => ({ type: CLEAR_PENDING_LISTING_ASSIGNMENTS });
export const clearPendingForCategory = categoryId => ({ type: CLEAR_PENDING_FOR_CATEGORY, payload: { categoryId } });
export const setListingCountOverrides = overrides => ({ type: SET_LISTING_COUNT_OVERRIDES, payload: { overrides } });
export const setCountsFromSave = (countsById, groupCountsByMpOption) => ({
    type: SET_COUNTS_FROM_SAVE,
    payload: { countsById, groupCountsByMpOption },
});
export const setLastSaved = (categories, groups, showAllCategories, showAllCustomCategories, showAllInCategoryFilter) => ({
    type: SET_LAST_SAVED,
    payload: { categories, groups, showAllCategories, showAllCustomCategories, showAllInCategoryFilter },
});
export const setShowAllCategories = showAllCategories => ({ type: SET_SHOW_ALL_CATEGORIES, payload: { showAllCategories } });
export const setShowAllCustomCategories = showAllCustomCategories => ({ type: SET_SHOW_ALL_CUSTOM_CATEGORIES, payload: { showAllCustomCategories } });
export const setShowAllInCategoryFilter = showAllInCategoryFilter => ({
    type: SET_SHOW_ALL_IN_CATEGORY_FILTER,
    payload: { showAllInCategoryFilter },
});
export const incrementListingAssignmentRefresh = () => ({ type: INCREMENT_LISTING_ASSIGNMENT_REFRESH });
export const addAssignmentFailures = failures => ({ type: ADD_ASSIGNMENT_FAILURES, payload: failures });
export const dismissAssignmentFailure = id => ({ type: DISMISS_ASSIGNMENT_FAILURE, payload: { id } });
export const clearAssignmentFailures = () => ({ type: CLEAR_ASSIGNMENT_FAILURES });

function removeIds(list, idsToRemove) {
    if (!Array.isArray(list) || list.length === 0) return [];
    const set = new Set(idsToRemove || []);
    if (set.size === 0) return list;
    return list.filter(id => !set.has(id));
}

function mapFailuresToNotifications({ failures, categoryId }) {
    const list = Array.isArray(failures) ? failures : [];
    return list.map(f => ({
        id: uuidv4(),
        listingId: f.listingId,
        title: f.title || '',
        categoryId,
        type: f.type,
        message: f.message || 'Failed to update listing assignment',
    }));
}

function isFullChunkSuccess(res) {
    // We treat "success with no failures" as "all requested IDs are settled",
    // even if the server did no writes (no-op because listing already matched desired state).
    return !!(res && res.success === true && Array.isArray(res.failedOperations) && res.failedOperations.length === 0);
}

// Selector: true when there are page-level unsaved changes (reorder, visibility, showAllCategories, showAllCustomCategories, showAllInCategoryFilter, or pending listing assignments)
export const selectHasUnsavedChanges = state => {
    const {
        categories,
        groups,
        showAllCategories,
        showAllCustomCategories,
        showAllInCategoryFilter,
        lastSavedCategories,
        lastSavedGroups,
        lastSavedShowAllCategories,
        lastSavedShowAllCustomCategories,
        lastSavedShowAllInCategoryFilter,
        pendingListingAssignments,
    } = state.StoreCategoriesPage || {};
    const pendingKeys = Object.keys(pendingListingAssignments || {});
    return (
        !isEqual(categories, lastSavedCategories) ||
        !isEqual(groups, lastSavedGroups) ||
        showAllCategories !== lastSavedShowAllCategories ||
        showAllCustomCategories !== lastSavedShowAllCustomCategories ||
        showAllInCategoryFilter !== lastSavedShowAllInCategoryFilter ||
        pendingKeys.length > 0
    );
};

// ================ Counts thunk ================ //
export const loadCounts = () => async (dispatch, getState) => {
    dispatch(loadCountsRequest());
    try {
        const resp = await getStoreCategoryCounts();
        // resp may already be JSON
        const payload = resp?.data || resp; // handle transit/json differences
        dispatch(loadCountsSuccess(payload));
    } catch (e) {
        dispatch(loadCountsError(storableError(e)));
    }
};

// ================ Helpers ================ //

/** Build orderedCategoryIds in tree order (depth-first; siblings sorted by order). Uses groups.__order for top-level mp keys. */
function buildOrderedCategoryIds(categories, groups) {
    const keyForParent = (p) => (p == null || p === undefined ? 'null' : p);
    const byParent = new Map();
    (categories || []).forEach(c => {
        const key = keyForParent(c.parentId);
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(c);
    });
    byParent.forEach(arr => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    const mpKeys = [...byParent.keys()].filter(k => typeof k === 'string' && k.startsWith('mp:'));
    const savedOrder = Array.isArray(groups?.__order) ? groups.__order : [];
    const orderedTopKeys = [
        ...savedOrder.filter(k => mpKeys.includes(k)),
        ...mpKeys.filter(k => !savedOrder.includes(k)),
    ];
    if (byParent.has('null')) orderedTopKeys.unshift('null');
    const list = [];
    function visit(parentKey) {
        const siblings = byParent.get(parentKey) || [];
        siblings.forEach(c => {
            list.push(c.id);
            visit(`sc:${c.id}`);
        });
    }
    orderedTopKeys.forEach(k => visit(k));
    return list;
}

/** Build delta payload and optional newCategories from current vs lastSaved. Returns { delta, newCategories } or null if cannot use delta. */
function buildDeltaPayload(state) {
    const { categories, groups, showAllCategories, showAllCustomCategories, showAllInCategoryFilter, lastSavedCategories, lastSavedGroups, lastSavedShowAllCategories, lastSavedShowAllCustomCategories, lastSavedShowAllInCategoryFilter } = state.StoreCategoriesPage || {};
    const lastIds = new Set((lastSavedCategories || []).map(c => c.id));
    const currentIds = new Set((categories || []).map(c => c.id));
    const deletedCategoryIds = (lastSavedCategories || []).filter(c => !currentIds.has(c.id)).map(c => c.id);
    const newCategories = (categories || []).filter(c => !lastIds.has(c.id));
    const orderedCategoryIds = buildOrderedCategoryIds(categories, groups);
    const visibilityChanges = {};
    (categories || []).forEach(c => {
        const last = (lastSavedCategories || []).find(l => l.id === c.id);
        if (last && typeof c.visible === 'boolean' && c.visible !== last.visible) visibilityChanges[c.id] = c.visible;
    });
    const groupOrder = (groups && groups.__order) || undefined;
    const groupVisibility = {};
    if (groups && lastSavedGroups) {
        const mpKeys = [...new Set([...Object.keys(groups), ...Object.keys(lastSavedGroups)].filter(k => k !== '__order'))];
        mpKeys.forEach(k => {
            if (groups[k]?.visible !== lastSavedGroups[k]?.visible) groupVisibility[k] = groups[k]?.visible;
        });
    }
    const delta = {
        orderedCategoryIds,
        deletedCategoryIds,
        visibilityChanges: Object.keys(visibilityChanges).length ? visibilityChanges : {},
        showAllCategories: showAllCategories ?? true,
        showAllCustomCategories: showAllCustomCategories ?? true,
        showAllInCategoryFilter: showAllInCategoryFilter ?? true,
        baseLocale: 'en',
    };
    if (Array.isArray(groupOrder) && groupOrder.length > 0) delta.groupOrder = groupOrder;
    if (Object.keys(groupVisibility).length > 0) delta.groupVisibility = groupVisibility;
    return { delta, newCategories };
}

export const addCategory = ({ name, parentId = null, locale = 'en' }) => (dispatch, getState) => {
    const { categories, groups } = getState().StoreCategoriesPage;
    const siblings = categories.filter(c => c.parentId === parentId);
    const maxOrder = siblings.reduce((max, s) => Math.max(max, s.order || 0), 0);
    const nextOrder = maxOrder + 1;
    const newCategory = { id: uuidv4(), name, names: { [locale]: name }, parentId, order: nextOrder, visible: true };
    dispatch(reorderCategories([...categories, newCategory]));
    // If creating under a marketplace group, append that group to order if missing
    if (typeof parentId === 'string' && parentId.startsWith('mp:')) {
        const prevOrder = Array.isArray(groups?.__order) ? groups.__order : [];
        if (!prevOrder.includes(parentId)) {
            const nextOrderArr = [...prevOrder, parentId];
            dispatch(reorderGroups({ ...(groups || {}), __order: nextOrderArr }));
        }
    }
    return newCategory;
};

// Helper to collect all descendants of a category id (store category only)
const collectDescendants = (categories, rootId) => {
    const idToChildren = new Map();
    categories.forEach(c => {
        const p = c.parentId;
        if (typeof p === 'string' && p.startsWith('sc:')) {
            const pid = p.slice(3);
            const arr = idToChildren.get(pid) || [];
            arr.push(c.id);
            idToChildren.set(pid, arr);
        }
    });
    const result = [];
    const stack = [rootId];
    while (stack.length) {
        const id = stack.pop();
        const children = idToChildren.get(id) || [];
        result.push(...children);
        stack.push(...children);
    }
    return result;
};

// Helper: collect all descendants under a marketplace key
const collectUnderMarketplace = (categories, mpKey) => {
    const topIds = categories.filter(c => c.parentId === mpKey).map(c => c.id);
    const all = new Set(topIds);
    topIds.forEach(id => collectDescendants(categories, id).forEach(d => all.add(d)));
    return Array.from(all);
};

// Thunk: cascade hide for a marketplace key or a store category id
export const cascadeHide = (keyOrId) => (dispatch, getState) => {
    const { categories } = getState().StoreCategoriesPage;
    if (typeof keyOrId === 'string' && keyOrId.startsWith('mp:')) {
        const mpKey = keyOrId;
        const ids = collectUnderMarketplace(categories, mpKey);
        dispatch(bulkSetVisibility(ids, false));
    } else {
        const rootId = keyOrId;
        const desc = collectDescendants(categories, rootId);
        dispatch(bulkSetVisibility(desc, false));
    }
};

// Thunk: cascade show (make visible) for a marketplace key or a store category id
export const cascadeShow = (keyOrId) => (dispatch, getState) => {
    const { categories } = getState().StoreCategoriesPage;
    if (typeof keyOrId === 'string' && keyOrId.startsWith('mp:')) {
        const ids = collectUnderMarketplace(categories, keyOrId);
        dispatch(bulkSetVisibility(ids, true));
    } else {
        const desc = collectDescendants(categories, keyOrId);
        dispatch(bulkSetVisibility(desc, true));
    }
};

// StoreCategories.duck.js
export const moveCategory = ({ sourceIndex, destinationIndex, parentId, draggableId }) =>
    (dispatch, getState) => {
        const { categories } = getState().StoreCategoriesPage;

        // Get all categories with the same parent
        const siblings = categories
            .filter(c => {
                if (parentId === null) return c.parentId === null || c.parentId === undefined;
                return c.parentId === parentId;
            })
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        // Create a new array for the reordered siblings
        const newSiblings = Array.from(siblings);
        const [movedItem] = newSiblings.splice(sourceIndex, 1);
        newSiblings.splice(destinationIndex, 0, movedItem);

        // Update orders for all siblings
        const updatedSiblings = newSiblings.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        // Create the final categories array
        const finalCategories = categories.map(cat => {
            const updatedCat = updatedSiblings.find(s => s.id === cat.id);
            return updatedCat || cat;
        });

        dispatch(reorderCategories(finalCategories));
    };

// Persist to user profile publicData (page save: reorder, visibility, optional newCategories, and optional batched listing assignments).
// Uses delta save (small payload); if there are pending listing assignments, runs batched assign-listings after profile save.
export const saveCategories = (baseLocale = 'en') => async (dispatch, getState, sdk) => {
    dispatch(saveRequest());
    try {
        const state = getState();
        const user = ensureCurrentUser(state.user.currentUser);
        const categories = state.StoreCategoriesPage.categories;
        const storeCategoryGroups = normalizeGroups(state.StoreCategoriesPage.groups || {}, categories);

        const { delta, newCategories } = buildDeltaPayload(state);
        const deltaPayload = {
            ...delta,
            baseLocale: baseLocale || delta.baseLocale,
            newCategories: (newCategories && newCategories.length > 0) ? newCategories : undefined,
        };

        await saveStoreCategoriesDelta(deltaPayload, SAVE_STORE_CATEGORIES_TIMEOUT_MS);

        // Persist any pending assign/unassign from the inline list (below categories) so removals and reorders are saved on page save
        const pendingListingAssignments = state.StoreCategoriesPage.pendingListingAssignments || {};
        let mergedCountsDelta = {};
        const nextPending = { ...(pendingListingAssignments || {}) };
        const failureNotifications = [];
        if (Object.keys(pendingListingAssignments).length > 0) {
            const idToCat = new Map((categories || []).map(c => [c.id, c]));
            for (const categoryId of Object.keys(pendingListingAssignments)) {
                const depth = getCategoryDepth(categoryId, idToCat);
                if (depth < 1 || depth > 4) continue;
                const { assignListingIds = [], unassignListingIds = [] } = pendingListingAssignments[categoryId] || {};
                let appliedAssignIds = [];
                let appliedUnassignIds = [];
                let failedAssignIds = [];
                let failedUnassignIds = [];
                for (let i = 0; i < assignListingIds.length; i += ASSIGN_LISTINGS_MAX_BATCH_SIZE) {
                    const chunk = assignListingIds.slice(i, i + ASSIGN_LISTINGS_MAX_BATCH_SIZE);
                    if (chunk.length === 0) continue;
                    const res = await assignListings({
                        categoryId,
                        depth,
                        assignListingIds: chunk,
                        assignOrderStart: i,
                        unassignListingIds: [],
                    });
                    const applied = isFullChunkSuccess(res)
                        ? chunk
                        : (res?.appliedOperations || []).filter(o => o?.type === 'assign').map(o => o.listingId);
                    const failed = (res?.failedOperations || []).filter(o => o?.type === 'assign').map(o => o.listingId);
                    appliedAssignIds.push(...applied);
                    failedAssignIds.push(...failed);
                    failureNotifications.push(...mapFailuresToNotifications({ failures: res?.failedOperations, categoryId }));
                    if (res && res.countsDelta) {
                        Object.entries(res.countsDelta).forEach(([catId, d]) => {
                            mergedCountsDelta[catId] = (mergedCountsDelta[catId] || 0) + d;
                        });
                    }
                }
                for (let i = 0; i < unassignListingIds.length; i += ASSIGN_LISTINGS_MAX_BATCH_SIZE) {
                    const chunk = unassignListingIds.slice(i, i + ASSIGN_LISTINGS_MAX_BATCH_SIZE);
                    if (chunk.length === 0) continue;
                    const res = await assignListings({
                        categoryId,
                        depth,
                        assignListingIds: [],
                        unassignListingIds: chunk,
                    });
                    const applied = isFullChunkSuccess(res)
                        ? chunk
                        : (res?.appliedOperations || []).filter(o => o?.type === 'unassign').map(o => o.listingId);
                    const failed = (res?.failedOperations || []).filter(o => o?.type === 'unassign').map(o => o.listingId);
                    appliedUnassignIds.push(...applied);
                    failedUnassignIds.push(...failed);
                    failureNotifications.push(...mapFailuresToNotifications({ failures: res?.failedOperations, categoryId }));
                    if (res && res.countsDelta) {
                        Object.entries(res.countsDelta).forEach(([catId, d]) => {
                            mergedCountsDelta[catId] = (mergedCountsDelta[catId] || 0) + d;
                        });
                    }
                }

                // Keep only failed IDs pending (remove successfully applied IDs).
                // If a chunk had neither applied nor failed (e.g. no-ops), keep as-is.
                if ((appliedAssignIds.length || appliedUnassignIds.length) > 0) {
                    const prev = nextPending[categoryId] || { assignListingIds: [], unassignListingIds: [] };
                    const nextAssign = removeIds(prev.assignListingIds || [], appliedAssignIds);
                    const nextUnassign = removeIds(prev.unassignListingIds || [], appliedUnassignIds);
                    if ((nextAssign?.length || 0) === 0 && (nextUnassign?.length || 0) === 0) {
                        delete nextPending[categoryId];
                    } else {
                        nextPending[categoryId] = { assignListingIds: nextAssign, unassignListingIds: nextUnassign };
                    }
                }
            }
        }

        dispatch(currentUserShowSuccess(stripCategoryFieldsFromUserPublicData(user)));
        dispatch(fetchStoreCategoryConfig());

        const countsById = state.StoreCategoriesPage.countsById || {};
        if (Object.keys(mergedCountsDelta).length > 0) {
            const newCountsById = { ...countsById };
            Object.keys(mergedCountsDelta).forEach(catId => {
                newCountsById[catId] = Math.max(0, (newCountsById[catId] ?? 0) + mergedCountsDelta[catId]);
            });
            const groupCountsByMpOption = computeGroupCountsFromCountsById(
                state.StoreCategoriesPage.categories,
                state.StoreCategoriesPage.groups,
                newCountsById
            );
            dispatch(setCountsFromSave(newCountsById, groupCountsByMpOption));
        }
        // Only clear what was successfully applied; keep failed operations pending so user can re-save.
        dispatch({ type: CLEAR_PENDING_LISTING_ASSIGNMENTS });
        // Rehydrate pending with failures (if any)
        if (Object.keys(nextPending).length > 0) {
            // Replace pendingListingAssignments in one shot by dispatching per-category updates
            Object.keys(nextPending).forEach(categoryId => {
                const p = nextPending[categoryId];
                dispatch(setPendingListingAssignments(
                    categoryId,
                    p.assignListingIds || [],
                    p.unassignListingIds || [],
                    p.forceReassignListingIds || []
                ));
            });
        }
        if (failureNotifications.length > 0) {
            dispatch(addAssignmentFailures(failureNotifications));
        }
        dispatch(setLastSaved(
            state.StoreCategoriesPage.categories,
            state.StoreCategoriesPage.groups,
            state.StoreCategoriesPage.showAllCategories,
            state.StoreCategoriesPage.showAllCustomCategories,
            state.StoreCategoriesPage.showAllInCategoryFilter
        ));
        dispatch(saveSuccess());
        dispatch(incrementListingAssignmentRefresh());

        setTimeout(() => {
            dispatch(loadCounts());
            dispatch(loadSyncStatus());
        }, STORE_CATEGORY_SYNC_DELAY_MS);

        return  true;
    } catch (e) {
        return dispatch(saveError(storableError(e)));
    }
};

// Persist only the category and its listing assignments (modal save: no reorder/visibility).
// Uses lightweight save-category endpoint for profile change, then batched assign-listings for this category.
export const saveCategoryFromModal = ({ type, categoryId, baseLocale = 'en', assignmentDirty = false }) => async (dispatch, getState, sdk) => {
    dispatch(saveRequest());
    try {
        const state = getState();
        const { categories, groups, pendingListingAssignments } = state.StoreCategoriesPage;
        const user = ensureCurrentUser(state.user.currentUser);
        const categoryFromState = (categories || []).find(c => c.id === categoryId);
        if (!categoryFromState) {
            dispatch(saveError(storableError(new Error('Category not found'))));
            return;
        }

        // 1) Persist the category itself via lightweight endpoint
        await saveStoreCategory(
            {
                type,
                category: categoryFromState,
                baseLocale,
            },
            SAVE_STORE_CATEGORIES_TIMEOUT_MS
        );

        // 2) Apply pending listing assignments for this category only (batched)
        const pending = (pendingListingAssignments || {})[categoryId] || {};
        const { assignListingIds = [], unassignListingIds = [], forceReassignListingIds = [] } = pending;

        let mergedCountsDelta = {};
        const failureNotifications = [];
        let appliedAssignIds = [];
        let appliedUnassignIds = [];
        if (assignmentDirty && (assignListingIds.length || unassignListingIds.length) > 0) {
            const idToCat = new Map((categories || []).map(c => [c.id, c]));
            const depth = getCategoryDepth(categoryId, idToCat);
            if (depth >= 1 && depth <= 4) {
                // Assign in chunks
                for (let i = 0; i < assignListingIds.length; i += ASSIGN_LISTINGS_MAX_BATCH_SIZE) {
                    const chunk = assignListingIds.slice(i, i + ASSIGN_LISTINGS_MAX_BATCH_SIZE);
                    if (chunk.length === 0) continue;
                    const shouldForceReassignChunk = chunk.some(id => (forceReassignListingIds || []).includes(id));
                    const res = await assignListings({
                        categoryId,
                        depth,
                        assignListingIds: chunk,
                        assignOrderStart: i,
                        unassignListingIds: [],
                        forceReassignToAncestor: shouldForceReassignChunk,
                    });
                    if (isFullChunkSuccess(res)) {
                        appliedAssignIds.push(...chunk);
                    } else {
                        appliedAssignIds.push(...((res?.appliedOperations || []).filter(o => o?.type === 'assign').map(o => o.listingId)));
                    }
                    failureNotifications.push(...mapFailuresToNotifications({ failures: res?.failedOperations, categoryId }));
                    if (res && res.countsDelta) {
                        Object.entries(res.countsDelta).forEach(([catId, d]) => {
                            mergedCountsDelta[catId] = (mergedCountsDelta[catId] || 0) + d;
                        });
                    }
                }
                // Unassign in chunks
                for (let i = 0; i < unassignListingIds.length; i += ASSIGN_LISTINGS_MAX_BATCH_SIZE) {
                    const chunk = unassignListingIds.slice(i, i + ASSIGN_LISTINGS_MAX_BATCH_SIZE);
                    if (chunk.length === 0) continue;
                    const res = await assignListings({
                        categoryId,
                        depth,
                        assignListingIds: [],
                        unassignListingIds: chunk,
                    });
                    if (isFullChunkSuccess(res)) {
                        appliedUnassignIds.push(...chunk);
                    } else {
                        appliedUnassignIds.push(...((res?.appliedOperations || []).filter(o => o?.type === 'unassign').map(o => o.listingId)));
                    }
                    failureNotifications.push(...mapFailuresToNotifications({ failures: res?.failedOperations, categoryId }));
                    if (res && res.countsDelta) {
                        Object.entries(res.countsDelta).forEach(([catId, d]) => {
                            mergedCountsDelta[catId] = (mergedCountsDelta[catId] || 0) + d;
                        });
                    }
                }
            }
        }

        const storeCategoryGroups = normalizeGroups(groups || {}, categories);

        dispatch(currentUserShowSuccess(stripCategoryFieldsFromUserPublicData(user)));
        dispatch(fetchStoreCategoryConfig());

        const countsById = state.StoreCategoriesPage.countsById || {};
        if (Object.keys(mergedCountsDelta).length > 0) {
            const newCountsById = { ...countsById };
            Object.keys(mergedCountsDelta).forEach(catId => {
                newCountsById[catId] = Math.max(0, (newCountsById[catId] ?? 0) + mergedCountsDelta[catId]);
            });
            const groupCountsByMpOption = computeGroupCountsFromCountsById(
                categories,
                storeCategoryGroups,
                newCountsById
            );
            dispatch(setCountsFromSave(newCountsById, groupCountsByMpOption));
        }
        // Keep failed operations pending so user can re-save.
        if ((appliedAssignIds.length || appliedUnassignIds.length) > 0) {
            const prev = (getState().StoreCategoriesPage.pendingListingAssignments || {})[categoryId] || pending;
            const nextAssign = removeIds(prev.assignListingIds || [], appliedAssignIds);
            const nextUnassign = removeIds(prev.unassignListingIds || [], appliedUnassignIds);
            const nextForceReassign = removeIds(prev.forceReassignListingIds || [], appliedAssignIds);
            if ((nextAssign?.length || 0) === 0 && (nextUnassign?.length || 0) === 0) {
                dispatch(clearPendingForCategory(categoryId));
            } else {
                dispatch(setPendingListingAssignments(categoryId, nextAssign, nextUnassign, nextForceReassign));
            }
        }
        if (failureNotifications.length > 0) {
            dispatch(addAssignmentFailures(failureNotifications));
        }
        dispatch(setLastSaved(
            categories,
            storeCategoryGroups,
            state.StoreCategoriesPage.showAllCategories,
            state.StoreCategoriesPage.showAllCustomCategories,
            state.StoreCategoriesPage.showAllInCategoryFilter
        ));
        dispatch(saveSuccess());
        dispatch(incrementListingAssignmentRefresh());

        setTimeout(() => {
            dispatch(loadCounts());
            dispatch(loadSyncStatus());
        }, STORE_CATEGORY_SYNC_DELAY_MS);
    } catch (e) {
        dispatch(saveError(storableError(e)));
    }
};

// ================ Load data ================ //

export const loadData = () => async (dispatch, getState, sdk) => {
    dispatch(loadRequest());
    let { currentUser } = getState().user;

    // The load data function is called server side, 
    // during SSR the currentUser is not set, so we need to fetch it from the API
    if (!currentUser) {
        try {
            const cUser = await sdk.currentUser.show();
            currentUser = cUser.data.data;
        } catch (error) { }
    };

    try {
        const user = ensureCurrentUser(currentUser);
        const publicData = user.attributes?.profile?.publicData || {};
        await dispatch(fetchStoreCategoryConfig());
        const cfg = getState().user.storeCategoryConfig;
        const categories = Array.isArray(cfg?.storeCategories)
            ? cfg.storeCategories
            : (publicData.storeCategories || []);
        const groups = cfg?.storeCategoryGroups || publicData.storeCategoryGroups || {};
        // If there are no configured store categories, force Show All to true (marketplace-only mode).
        const showAllCategories = (Array.isArray(categories) && categories.length === 0) ? true : ((cfg?.showAllCategories ?? publicData.showAllCategories) !== false);
        const showAllCustomCategories = cfg?.showAllCustomCategories ?? publicData.showAllCustomCategories ?? true;
        const showAllInCategoryFilter = cfg?.showAllInCategoryFilter ?? publicData.showAllInCategoryFilter ?? true;
        dispatch(loadCategories(categories));
        dispatch(loadGroups(groups));
        dispatch(setShowAllCategories(showAllCategories));
        dispatch(setShowAllCustomCategories(showAllCustomCategories));
        dispatch(setShowAllInCategoryFilter(showAllInCategoryFilter));
        dispatch(setLastSaved(null, null, showAllCategories, showAllCustomCategories, showAllInCategoryFilter));
        dispatch(loadSuccess());
        // kick off counts after categories exist
        try { dispatch(loadCounts()); } catch (_) { }
        // Load sync status on page load
        try { dispatch(loadSyncStatus()); } catch (_) { }
    } catch (e) {
        dispatch(loadError(storableError(e)));
    }
    return Promise.resolve();
};

// ================ Load sync status ================ //

export const loadSyncStatus = () => async (dispatch, getState, sdk) => {
    dispatch({ type: LOAD_SYNC_STATUS_REQUEST });
    try {
        const result = await getStoreCategorySyncStatus();
        // Backend returns status object directly (not wrapped in data)
        // The get() helper returns the JSON response directly
        dispatch({ type: LOAD_SYNC_STATUS_SUCCESS, payload: result || {} });
    } catch (e) {
        dispatch({ type: LOAD_SYNC_STATUS_ERROR, payload: storableError(e) });
    }
};


// ================ Internal utils ================ //

/**
 * Compute groupCountsByMpOption from countsById and category tree (mirrors server/getStoreCategoryCounts and StoreCategories useMemo).
 */
function computeGroupCountsFromCountsById(categories, groups, countsById) {
    const keyForParent = parentId => (parentId == null ? 'null' : parentId);
    const siblingsByParent = new Map();
    (categories || []).forEach(c => {
        const key = keyForParent(c.parentId);
        const arr = siblingsByParent.get(key) || [];
        arr.push(c);
        siblingsByParent.set(key, arr);
    });
    const mpKeys = Array.from(siblingsByParent.keys()).filter(k => typeof k === 'string' && k.startsWith('mp:'));
    const savedOrder = Array.isArray(groups?.__order) ? groups.__order : [];
    const orderedKeys = [
        ...savedOrder.filter(k => mpKeys.includes(k)),
        ...mpKeys.filter(k => !savedOrder.includes(k)),
    ];
    const byMpOption = {};
    orderedKeys.forEach(mpKey => {
        const option = mpKey.startsWith('mp:') ? mpKey.slice(3) : mpKey;
        const children = siblingsByParent.get(mpKey) || [];
        byMpOption[option] = children.reduce((sum, node) => {
            const storeCount = (countsById && countsById[node.id]) || 0;
            const descendants = getAllDescendants(node.id, categories || []);
            const descendantsCount = descendants.reduce((s, descId) => s + ((countsById && countsById[descId]) || 0), 0);
            return sum + storeCount + descendantsCount;
        }, 0);
    });
    return byMpOption;
}

function normalizeGroups(incomingGroups, categories) {
    const safeIncoming = incomingGroups || {};
    const cats = categories || [];
    // compute mp keys that have at least one direct child
    const mpKeysWithChildren = Array.from(
        new Set(
            cats
                .filter(c => typeof c.parentId === 'string' && c.parentId.startsWith('mp:'))
                .map(c => c.parentId)
        )
    );

    const existingOrder = Array.isArray(safeIncoming.__order) ? safeIncoming.__order : [];
    // keep existing relative order for keys that still exist
    const filteredOrder = existingOrder.filter(k => mpKeysWithChildren.includes(k));

    // append any missing keys (newly created) to the end
    const missing = mpKeysWithChildren.filter(k => !filteredOrder.includes(k));
    let order = [...filteredOrder, ...missing];

    // If order still empty but there are groups, derive deterministic default by first appearance
    if (order.length === 0 && mpKeysWithChildren.length > 0) {
        const firstIndexByMp = {};
        cats.forEach((c, idx) => {
            if (typeof c.parentId === 'string' && c.parentId.startsWith('mp:')) {
                if (firstIndexByMp[c.parentId] == null) firstIndexByMp[c.parentId] = idx;
            }
        });
        order = mpKeysWithChildren
            .slice()
            .sort((a, b) => (firstIndexByMp[a] || 0) - (firstIndexByMp[b] || 0));
    }

    // Build next groups object, keeping only non-empty keys and copying their settings
    const nextGroups = mpKeysWithChildren.reduce((acc, k) => {
        if (safeIncoming[k]) acc[k] = { ...safeIncoming[k] };
        return acc;
    }, {});

    return { ...nextGroups, __order: order };
}

